import React from 'react';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { formatCurrency } from '../../../utils/baseUtils';
import { cn } from '../../../components/ui/_cn';
import { dash, dateCell, utcDateCell } from '../../finance/pages/finance-cells';
import { upcomingPayoutPath } from './upcoming-payout';
import FinBadge from './fin-badge';
import FinButton from './fin-button';
import { FinTableSkeleton } from './fin-skeleton';

// Stripe's own statuses, drawn as Stripe states them (spec 2026-09-21 §2):
// `pending` is created-not-sent, `in_transit` is sent. Instant is a method,
// tagged beside the status, never a status of its own.
const TONES = {
  paid: 'paid',
  pending: 'pending',
  in_transit: 'in-transit',
  failed: 'failed',
  canceled: 'canceled',
  // The Upcoming detail page's own status (spec 2026-09-22 §13) — its own
  // tone, never the canceled fallback, so its header badge reads "Upcoming"
  // rather than the wrong color entirely.
  upcoming: 'upcoming',
};

// A PAID instant payout wears only the solid green "Instant" tag (owner
// ruling 4; designer's change list, "Fix — Instant payout status tag"):
// production showed "Paid" + "Instant". Any other state keeps its status
// beside the tag. The list rows and the payout detail header share this rule.
export const wearsOnlyInstant = (payout) => payout?.method === 'instant' && payout?.status === 'paid';

// `returned` (API, 2026-10-07): a failed payout that had been paid — the
// bank sent it back. Its own badge, so a list never calls a deposit that
// bounced "Failed" as if it had never arrived (mock frame 5f).
export const payoutTone = (status, returned = false) =>
  returned && status === 'failed' ? 'returned' : TONES[status] || 'canceled';

// Two grids for two hosts (spec 2026-09-21 §4.2): the Overview card is half
// the content column, so its columns are narrower and its rows tighter; the
// Payouts card has the width for the mock's 2b template and its recessed
// header band. The table owns its header row so each template is declared
// exactly once.
// Below `sm` the To and Sent columns are not drawn (owner, 2026-09-22: on a
// phone they pushed Amount out of the card, behind a sideways scroll), so
// each variant carries a three-track phone template beside its own.
// `button` is a clickable row's width. A <div> row stretches into the compact
// row's 10px bleed on each side by itself; a <button> row does not, and at
// `w-full` the bleed's padding came out of the 1fr track, putting every column
// after To 20px left of its header (2026-10-05). So the compact width adds the
// two bleeds back.
// `scroller` carries the same bleed for the compact rows (2026-10-06). A row
// that reaches 10px past its scroll box on the right is overflow, so the card
// drew a sideways scrollbar under rows that all fitted, and the left 10px was
// clipped. With the box itself 10px wider on each side the row ends on its
// edge. `separator` then keeps the compact hairline on the columns, where the
// header's rule is, instead of running it out into the bleed with the row.
// `status` is where the Instant tag goes. Two badges need up to 131px
// ("In transit" + "Instant"): the Overview card has 96px and stacks them, the
// Payouts table has the room for a 136px column and wraps only on a phone.
const VARIANTS = {
  compact: {
    grid: 'grid grid-cols-[96px_minmax(0,1fr)_84px] sm:grid-cols-[96px_minmax(0,1fr)_64px_72px_84px] gap-x-3',
    minWidth: 'sm:min-w-[420px]',
    scroller: 'overflow-x-auto -mx-2.5 px-2.5',
    header: 'pb-2 pt-4 border-b border-fin-border',
    row: 'py-3 text-[13px] -mx-2.5 px-2.5 rounded-lg',
    separator:
      "relative after:pointer-events-none after:absolute after:inset-x-2.5 after:bottom-0 after:border-b after:border-fin-hair after:content-['']",
    button: 'w-[calc(100%+1.25rem)]',
    status: 'flex flex-col items-start gap-1',
    bank: 'text-[13px]',
  },
  full: {
    grid: 'grid grid-cols-[96px_minmax(0,1fr)_96px] sm:grid-cols-[136px_minmax(0,1fr)_110px_110px_120px] gap-x-4',
    minWidth: 'sm:min-w-[586px]',
    scroller: 'overflow-x-auto',
    header: 'bg-fin-recess border-y border-fin-border px-5 py-2.5',
    row: 'px-5 py-[13px] text-[13.5px]',
    separator: 'border-b border-fin-hair',
    button: 'w-full',
    status: 'flex flex-wrap items-center gap-x-1.5 gap-y-1',
    bank: 'text-[13px]',
  },
};

// "Arrive by" (owner, 2026-09-23): the date is Stripe's expected arrival —
// the day the money should be in the bank by — not a promise it lands on it.
const HEADERS = ['Status', 'To', 'Sent', 'Arrive by', 'Amount'];
// Drawn from `sm` up only; the cells below carry the same class.
const PHONE_HIDDEN = new Set(['To', 'Sent']);
const PHONE_HIDDEN_CLS = 'hidden sm:block';

// The bank cell, shared by a real row and the synthetic Upcoming row (spec
// 2026-09-22 §11) — extracted so the two markups cannot drift apart.
const BankCell = ({ name, last4, bankCls }) =>
  name ? (
    <>
      <p className={cn('truncate font-semibold text-fin-ink', bankCls)}>{name}</p>
      {last4 ? <p className="mt-px text-[12px] font-medium text-fin-muted">{`•••• ${last4}`}</p> : null}
    </>
  ) : (
    dash()
  );

const Cells = ({ row, clickable, bankCls, statusCls }) => (
  <>
    <div role="gridcell" className={statusCls}>
      {wearsOnlyInstant(row) ? null : <FinBadge tone={payoutTone(row.status, row.returned)} />}
      {row.method === 'instant' ? <FinBadge tone="instant" /> : null}
    </div>
    <div role="gridcell" className={cn('min-w-0 overflow-hidden', PHONE_HIDDEN_CLS)}>
      <BankCell name={row.destinationBankName} last4={row.destinationLast4} bankCls={bankCls} />
    </div>
    <div role="gridcell" className={PHONE_HIDDEN_CLS}>{dateCell(row.createdAt)}</div>
    <div role="gridcell">{utcDateCell(row.arrivalDate)}</div>
    <div role="gridcell" className="flex items-center justify-end gap-1.5 font-bold tabular-nums text-fin-ink">
      {formatCurrency(row.amount)}
      {clickable ? <ChevronRightIcon className="h-3 w-3 stroke-[2.5] text-fin-chrome" aria-hidden="true" /> : null}
    </div>
  </>
);

// An Upcoming row: money that is not yet inside any Stripe payout (spec
// 2026-09-22 §F, `upcomingPayouts`). Sent is always unknown — no row here is
// a real payout — but Arrive by reads the group's own expected date, dash
// only for the dateless fallback row. It opens its own detail page (spec
// 2026-09-22 §13) exactly the way a real row does, so it takes the same
// `clickable` chevron.
// A shortfall row (owner, 2026-09-23) is still an Upcoming row: the next
// orders collect in it until it turns positive and takes a date. Until then
// its amount is red and it names no arrival date, because nothing arrives —
// Stripe takes it from the next sales. It opens its own page, which lists
// the order and the refund that made it.
const UpcomingCells = ({ upcoming, bankCls, statusCls, clickable }) => (
  <>
    <div role="gridcell" className={statusCls}>
      <FinBadge tone="upcoming" />
    </div>
    <div role="gridcell" className={cn('min-w-0 overflow-hidden', PHONE_HIDDEN_CLS)}>
      <BankCell name={upcoming.destinationBankName} last4={upcoming.destinationLast4} bankCls={bankCls} />
    </div>
    <div role="gridcell" className={PHONE_HIDDEN_CLS}>{dash()}</div>
    <div role="gridcell">{upcoming.shortfall ? dash() : utcDateCell(upcoming.arrivalDate)}</div>
    <div
      role="gridcell"
      className={cn(
        'flex items-center justify-end gap-1.5 font-bold tabular-nums',
        upcoming.shortfall ? 'text-fin-red' : 'text-fin-ink',
      )}
    >
      {formatCurrency(upcoming.amount)}
      {clickable ? <ChevronRightIcon className="h-3 w-3 stroke-[2.5] text-fin-chrome" aria-hidden="true" /> : null}
    </div>
  </>
);

const PayoutActivityTable = ({
  rows = [],
  loading = false,
  error = '',
  onRetry,
  empty,
  onOpen,
  variant = 'compact',
  ariaLabel = 'Payouts',
  // The synthetic leading rows (spec 2026-09-22 §F): `upcomingPayouts`'s
  // array, one row per expected arrival date, empty for a company on a
  // manual schedule or with nothing to bring forward. Never a real Stripe
  // payout — they take part in no filter, count, page or export, only the
  // row-count rules below.
  upcoming = [],
}) => {
  const v = VARIANTS[variant] || VARIANTS.compact;

  // A fixed body height under the headers (spec 2026-09-22 §3.3): five rows,
  // one row, none, the skeleton and the error line all occupy the same box,
  // so the card never resizes between one read and the next.
  // Only where the two Overview cards sit side by side (`lg:grid-cols-2`) do
  // they need the same box; in one column (a phone) the compact card hugs its
  // rows instead of leaving ~100px of air under five. The full variant is the
  // Payouts page's own list and keeps the box at every width, so its
  // skeleton, one-row and short pages do not jump.
  const BODY = variant === 'full' ? 'min-h-[320px]' : 'lg:min-h-[320px]';
  const CENTRED = 'flex min-h-[320px] items-center justify-center gap-3 text-center';

  if (error) {
    return (
      <div className={cn(CENTRED, variant === 'full' && 'px-5')}>
        <p className="text-[13px] text-fin-red">{error}</p>
        <FinButton onClick={onRetry}>Retry</FinButton>
      </div>
    );
  }
  if (loading && rows.length === 0) {
    return (
      <div className={BODY}>
        <FinTableSkeleton rows={5} />
      </div>
    );
  }

  return (
    <div className={v.scroller}>
      <div className={v.minWidth}>
        <div role="grid" aria-label={ariaLabel}>
          <div className={cn(v.grid, v.header, 'text-[12px] font-medium text-fin-faint')} role="row">
            {HEADERS.map((h) => (
              <div
                key={h}
                role="columnheader"
                className={cn(h === 'Amount' && 'text-right', PHONE_HIDDEN.has(h) && PHONE_HIDDEN_CLS) || undefined}
              >
                {h}
              </div>
            ))}
          </div>
          {/* rowgroup, not a bare div: owned children of role=grid must be
              row or rowgroup, and the min-height belongs to the body alone —
              the header row sits outside the 320px. */}
          <div role="rowgroup" className={rows.length > 0 || upcoming.length > 0 ? BODY : undefined}>
            {/* The Upcoming rows are rows 0..n-1 of [...upcoming, ...rows] for
                the border rule (spec §F/§11): each draws a border unless it
                is the last row on the whole grid. Each opens its own detail
                page (spec 2026-09-22 §13) exactly the way a real row does —
                the same interactive-grid shape, gated on `onOpen` the same
                way — and stays a static <div role="row"> only when no
                onOpen is given. */}
            {upcoming.map((group, index) => {
              const upcomingClickable = !!onOpen;
              const isLast = index === upcoming.length - 1 && rows.length === 0;
              const upcomingCls = cn(
                v.grid,
                v.row,
                'items-center text-left',
                !isLast && v.separator,
                upcomingClickable && 'hover:bg-fin-hover',
              );
              const key = group.id || `upcoming-${index}`;
              return upcomingClickable ? (
                <button
                  key={key}
                  type="button"
                  role="row"
                  className={cn(upcomingCls, v.button)}
                  onClick={() => onOpen(upcomingPayoutPath(group))}
                >
                  <UpcomingCells upcoming={group} bankCls={v.bank} statusCls={v.status} clickable />
                </button>
              ) : (
                <div key={key} role="row" className={upcomingCls}>
                  <UpcomingCells upcoming={group} bankCls={v.bank} statusCls={v.status} clickable={false} />
                </div>
              );
            })}
            {rows.map((row, index) => {
              const clickable = !!onOpen && !!row.stripePayoutId;
              const cls = cn(
                v.grid,
                v.row,
                'items-center text-left',
                index < rows.length - 1 && v.separator,
                clickable && 'hover:bg-fin-hover',
              );
              const key = row.id || `row-${index}`;
              // The interactive-grid shape (spec 2026-09-21 review, fix round 1):
              // a clickable row is a <button role="row"> — focusable and
              // Enter/Space-activated via the native button, its accessible
              // name still computed from its content — rather than the static
              // table pattern the other two grids use.
              return clickable ? (
                <button key={key} type="button" role="row" className={cn(cls, v.button)} onClick={() => onOpen(row.stripePayoutId)}>
                  <Cells row={row} clickable bankCls={v.bank} statusCls={v.status} />
                </button>
              ) : (
                <div key={key} role="row" className={cls}>
                  <Cells row={row} clickable={false} bankCls={v.bank} statusCls={v.status} />
                </div>
              );
            })}
          </div>
        </div>
        {/* Owned children of role=grid must be row/rowgroup; the empty
            message is a sibling of the grid, not inside it, so a screen
            reader does not prune it from the accessibility tree (spec
            2026-09-21 review, final wave item 1/6/16). */}
        {rows.length === 0 && upcoming.length === 0 ? (
          <div className={cn(CENTRED, variant === 'full' && 'px-5')}>
            <p className="text-[13px] text-fin-muted">{empty}</p>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default PayoutActivityTable;
